import { lazy } from "react";

import IntegrationsView from "views/settings-integration";
import { GoogleAnalyticsView } from "views/settings-integration/integrations/google-analytics";
import { HubSpotView } from "views/settings-integration/integrations/hubspot";
import { CodeboxView } from "views/settings-integration/integrations/codebox";
import { SentriLockView } from "views/settings-integration/integrations/sentrilock";
import { ZillowView } from "views/settings-integration/integrations/zillow";
import { ZumperView } from "views/settings-integration/integrations/zumper";
import { ApartmentsComView } from "views/settings-integration/integrations/apartments-com";
import { MHVillageView } from "views/settings-integration/integrations/mhvillage";
import { MetaAdsView } from "views/settings-integration/integrations/meta-ads";
import { BoomFinanceView } from "views/settings-integration/integrations/boom-finance";
import type { PartnerUserPermissions } from "providers/Auth";

import type { RouteConfig } from "../types";

const SettingsView = lazy(() => import("views/settings"));
const Profile = lazy(() => import("views/settings-profile"));
const ApiView = lazy(() => import("views/settings-api"));
const PmsView = lazy(
  () => import("views/settings-integration/integrations/pms")
);
const PmsIntegrationView = lazy(
  () => import("views/settings-integration/integrations/pms-integration")
);
const UsersView = lazy(() => import("views/settings-users"));
const UserPermissionsView = lazy(() => import("views/settings-users-id"));
const RoleDetailView = lazy(() => import("views/settings-roles-id"));
const AgentDetailView = lazy(() => import("views/settings-agents-id"));
const LeasingAgentDetailView = lazy(
  () => import("views/settings-leasing-agents-id")
);
const AddAgentView = lazy(() => import("views/settings-users-add-agent"));
const TrainingContextView = lazy(
  () => import("views/settings-users-training-context")
);
const NotificationsView = lazy(() => import("views/settings-notifications"));
const FeaturesView = lazy(() => import("views/settings-features"));
const SdkFeaturesView = lazy(() => import("views/settings-sdk-features"));
const ApplicationView = lazy(() => import("views/settings-application"));
const CommunicationView = lazy(() => import("views/settings-communication"));
const EmailTemplateFormView = lazy(() => import("views/email-template-form"));
const SettingsCRMView = lazy(() => import("views/settings-crm"));
const BillingView = lazy(() => import("views/billing"));
const BillingConfigurationView = lazy(
  () => import("views/settings-billing-configuration")
);
const StatementsIdView = lazy(() => import("views/statements-id"));
const BillingServicePeriodView = lazy(
  () => import("views/settings-billing-service-period")
);
const UpcomingStatementView = lazy(() => import("views/upcoming-statement"));
const SettingsShowingView = lazy(() => import("views/settings-showings"));
const SettingsShowingsAccountView = lazy(
  () => import("views/settings-showings-account")
);
const SettingsShowingsPersonalView = lazy(
  () => import("views/settings-showings-personal")
);
const SettingsShowingsPersonalPreviewView = lazy(
  () => import("views/settings-showings-personal-preview")
);
const ApplicantExperienceView = lazy(
  () => import("views/settings-applicant-experience")
);

type PartnerType = {
  isBoomScreen: boolean;
  isBoomCRM: boolean;
  isSDK: boolean;
  adminPrivileges: boolean;
};

export const getSettingsRoutes = (
  type: PartnerType,
  permissions: PartnerUserPermissions
): RouteConfig[] => [
  { path: "", index: true, component: SettingsView },

  // Admin
  { path: "api", component: ApiView, when: type.adminPrivileges },
  {
    path: "integration",
    when: type.adminPrivileges,
    children: [
      { path: "", index: true, component: IntegrationsView },
      { path: "pms", component: PmsView },
      { path: "pms/:integrationId", component: PmsIntegrationView },
      { path: "google-analytics", component: GoogleAnalyticsView },
      { path: "hubspot", component: HubSpotView },
      { path: "codebox", component: CodeboxView },
      { path: "sentrilock", component: SentriLockView },
      { path: "zillow", component: ZillowView },
      { path: "zumper", component: ZumperView },
      { path: "apartments-com", component: ApartmentsComView },
      { path: "mhvillage", component: MHVillageView },
      { path: "meta-ads", component: MetaAdsView, when: type.isBoomCRM },
      { path: "boom-finance", component: BoomFinanceView }
    ]
  },
  {
    path: "application",
    component: ApplicationView,
    when: type.adminPrivileges
  },
  { path: "features", component: FeaturesView, when: type.adminPrivileges },
  {
    path: "applicant-experience",
    component: ApplicantExperienceView,
    when: type.isBoomScreen && type.adminPrivileges
  },

  // Users & Roles
  {
    path: "users",
    component: UsersView,
    when: permissions.canAccessUsersSettings
  },
  {
    path: "users/:userId",
    component: UserPermissionsView,
    when: permissions.canAccessUsersSettings
  },
  {
    path: "roles/:roleId",
    component: RoleDetailView,
    when: permissions.canAccessUsersSettings
  },
  {
    path: "agents/:agentId",
    component: AgentDetailView,
    when: permissions.canAccessUsersSettings
  },
  {
    path: "users/add-agent",
    component: AddAgentView,
    when: permissions.canAccessUsersSettings
  },
  {
    path: "users/training-context/*",
    component: TrainingContextView,
    when: permissions.canAccessUsersSettings
  },
  {
    path: "leasing-agents/:agentId",
    component: LeasingAgentDetailView,
    when: permissions.canAccessUsersSettings
  },

  // Billing
  {
    path: "billing",
    component: BillingView,
    when: permissions.canAccessBillingSettings
  },
  {
    path: "billing/configuration",
    component: BillingConfigurationView,
    when: permissions.canAccessBillingSettings
  },
  {
    path: "billing/upcoming",
    component: UpcomingStatementView,
    when: permissions.canAccessBillingSettings
  },
  {
    path: "billing/statements/:statementId",
    component: StatementsIdView,
    when: permissions.canAccessBillingSettings
  },
  {
    path: "billing/service-periods/:servicePeriodDate",
    component: BillingServicePeriodView,
    when: permissions.canAccessBillingSettings
  },
  {
    path: "billing/service-periods/:servicePeriodDate/statements/:statementId",
    component: StatementsIdView,
    when: permissions.canAccessBillingSettings
  },

  // SDK
  { path: "sdk-features", component: SdkFeaturesView, when: type.isSDK },

  // Profile & Notifications
  { path: "profile", component: Profile },
  { path: "notifications", component: NotificationsView },

  // Showings
  {
    path: "showings",
    component: SettingsShowingView,
    when: type.isBoomCRM
  },
  {
    path: "showings/account",
    component: SettingsShowingsAccountView,
    when: type.isBoomCRM
  },
  {
    path: "showings/personal",
    component: SettingsShowingsPersonalView,
    when: type.isBoomCRM
  },
  {
    path: "showings/personal/preview",
    component: SettingsShowingsPersonalPreviewView,
    when: type.isBoomCRM
  },

  // CRM
  { path: "crm", component: SettingsCRMView, when: type.isBoomCRM },
  {
    path: "communication",
    component: CommunicationView,
    when: type.isBoomCRM
  },
  {
    path: "communication/email-templates/create",
    component: EmailTemplateFormView,
    when: type.isBoomCRM
  },
  {
    path: "communication/email-templates/:id/edit",
    component: EmailTemplateFormView,
    when: type.isBoomCRM
  }
];
